if ( typeof ProductImageZoom !== 'function' ) {

	class ProductImageZoom extends HTMLElement {

		constructor(){
			super();
			this.triggerBound = false;
			this.spacePressed = false;
			this.handleTriggerClick = this.handleTriggerClick.bind(this);
			this.onTriggerKeydown = this.onTriggerKeydown.bind(this);
			this.onTriggerKeyup = this.onTriggerKeyup.bind(this);
			this.onTriggerBlur = this.onTriggerBlur.bind(this);
		}

		connectedCallback(){
			this.ensureZoom();
			if ( this.triggerBound ) return;

			this.trigger = this.closest('[data-mf-zoom-trigger]');
			if ( !this.trigger ) return;

			this.trigger.addEventListener('click', this.handleTriggerClick);
			this.trigger.addEventListener('keydown', this.onTriggerKeydown);
			this.trigger.addEventListener('keyup', this.onTriggerKeyup);
			this.trigger.addEventListener('blur', this.onTriggerBlur);
			this.triggerBound = true;
		}

		disconnectedCallback(){
			if ( this.trigger && this.triggerBound ) {
				this.trigger.removeEventListener('click', this.handleTriggerClick);
				this.trigger.removeEventListener('keydown', this.onTriggerKeydown);
				this.trigger.removeEventListener('keyup', this.onTriggerKeyup);
				this.trigger.removeEventListener('blur', this.onTriggerBlur);
			}
			this.triggerBound = false;
			this.spacePressed = false;

			if ( this.zoom && this.zoom.mfZoomSession && this.zoom.mfZoomSession.owner === this ) {
				this.zoom.mfZoomSession.close(false);
			}
		}

		handleTriggerClick(event){
			if ( event.defaultPrevented ) return;
			this._open();
		}

		onTriggerKeydown(event){
			if ( event.repeat ) {
				if ( event.key === 'Enter' || event.key === ' ' ) event.preventDefault();
				return;
			}
			if ( event.key === 'Enter' ) {
				event.preventDefault();
				this._open();
			} else if ( event.key === ' ' ) {
				event.preventDefault();
				this.spacePressed = true;
			}
		}

		onTriggerKeyup(event){
			if ( event.key === ' ' && this.spacePressed ) {
				event.preventDefault();
				this.spacePressed = false;
				this._open();
			}
		}

		onTriggerBlur(){
			this.spacePressed = false;
		}

		ensureZoom(){
			let zoom = document.getElementById('zoom');
			if ( !zoom ) {
				zoom = document.createElement('div');
				zoom.id = 'zoom';
				zoom.innerHTML = `
					<div class="zoom__backdrop"></div>
					<div class="zoom__dialog" role="document">
						<button type="button" class="zoom__close">${KROWN.settings.symbols.zoom_out}</button>
						<div class="zoom__container">
							<img class="zoom__image" draggable="false" alt="" />
						</div>
						<span class="zoom__loader" aria-hidden="true">${KROWN.settings.symbols.zoom_loader}</span>
						<button type="button" class="zoom__nav zoom__nav--prev" disabled>
							<svg aria-hidden="true" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
						</button>
						<button type="button" class="zoom__nav zoom__nav--next" disabled>
							<svg aria-hidden="true" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
						</button>
						<div class="zoom__counter" aria-live="polite" aria-atomic="true"></div>
						<div class="zoom__error" role="status" aria-live="assertive" aria-atomic="true" hidden>
							<p class="zoom__error-text"></p>
							<button type="button" class="zoom__retry button button--solid"></button>
						</div>
						<div class="zoom__hint" aria-hidden="true">
							<svg aria-hidden="true" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M10 4V16M4 10H16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
							</svg>
							<span class="zoom__hint-text"></span>
						</div>
					</div>`;
				zoom.setAttribute('role', 'dialog');
				zoom.setAttribute('aria-modal', 'true');
				zoom.setAttribute('aria-hidden', 'true');
				zoom.setAttribute('tabindex', '-1');
				document.body.append(zoom);
			}

			this.zoom = zoom;
			return zoom;
		}

		getFocusable(){
			return [...this.zoom.querySelectorAll('button:not([disabled])')]
				.filter((node)=>{
					const style = getComputedStyle(node);
					return style.display !== 'none'
						&& style.visibility !== 'hidden'
						&& node.getClientRects().length > 0;
				});
		}

		setBackgroundInert(inert){
			[...document.body.children].forEach((node)=>{
				if ( node === this.zoom ) return;
				if ( inert ) {
					if ( node.dataset.mfZoomInertManaged === 'true' ) return;
					node.dataset.mfZoomWasInert = node.inert ? 'true' : 'false';
					node.dataset.mfZoomInertManaged = 'true';
					node.inert = true;
				} else if ( node.dataset.mfZoomInertManaged === 'true' ) {
					node.inert = node.dataset.mfZoomWasInert === 'true';
					delete node.dataset.mfZoomWasInert;
					delete node.dataset.mfZoomInertManaged;
				}
			});
		}

		_open(){
			const owner = this;
			const modal = owner.ensureZoom();

			if ( modal.mfZoomSession && !modal.mfZoomSession.closed ) {
				if ( modal.mfZoomSession.owner === owner ) return;
				modal.mfZoomSession.close(false);
			}

			const session = {
				token: Symbol('mf-zoom-session'),
				owner,
				closed: false,
				loadRevision: 0,
				gesturesBound: false,
				hintTimer: null
			};
			modal.mfZoomSession = session;

			const dialog = modal.querySelector('.zoom__dialog');
			const container = modal.querySelector('.zoom__container');
			const image = modal.querySelector('.zoom__image');
			const backdrop = modal.querySelector('.zoom__backdrop');
			const closeBtn = modal.querySelector('.zoom__close');
			const hint = modal.querySelector('.zoom__hint');
			const hintText = modal.querySelector('.zoom__hint-text');
			const prevBtn = modal.querySelector('.zoom__nav--prev');
			const nextBtn = modal.querySelector('.zoom__nav--next');
			const counterEl = modal.querySelector('.zoom__counter');
			const errorEl = modal.querySelector('.zoom__error');
			const errorText = modal.querySelector('.zoom__error-text');
			const retryBtn = modal.querySelector('.zoom__retry');

			modal.setAttribute('role', 'dialog');
			modal.setAttribute('aria-modal', 'true');
			modal.setAttribute('aria-label', owner.dataset.dialogLabel || '');
			closeBtn.setAttribute('aria-label', owner.dataset.closeLabel || '');
			prevBtn.setAttribute('aria-label', owner.dataset.previousLabel || '');
			nextBtn.setAttribute('aria-label', owner.dataset.nextLabel || '');
			retryBtn.textContent = owner.dataset.retryLabel || '';

			const productRoot = owner.closest('product-page');
			const allZoomEls = productRoot
				? [...productRoot.querySelectorAll('product-image-zoom[data-image]')]
				: [owner];
			const allImages = allZoomEls.map((element)=>({
				src: element.dataset.image,
				alt: element.dataset.imageAlt || ''
			}));
			let currentIndex = allZoomEls.indexOf(owner);
			if ( currentIndex === -1 ) currentIndex = 0;
			const hasMultiple = allImages.length > 1;

			prevBtn.style.display = hasMultiple ? '' : 'none';
			nextBtn.style.display = hasMultiple ? '' : 'none';
			counterEl.style.display = hasMultiple ? '' : 'none';

			let scale = 1;
			const minScale = 1;
			const maxScale = 4;
			let translateX = 0;
			let translateY = 0;
			let isDragging = false;
			let dragStartX = 0;
			let dragStartY = 0;
			let startTransX = 0;
			let startTransY = 0;
			let lastTouchDist = 0;
			let pinchStartScale = 1;
			let lastTapTime = 0;
			let swipeStartX = 0;
			let swipeEndX = 0;
			let isSwiping = false;

			const isTouch = document.body.classList.contains('touch')
				|| document.body.classList.contains('touchevents');
			hintText.textContent = isTouch ? owner.dataset.hintTouch : owner.dataset.hintPointer;

			function sessionIsCurrent(){
				return !session.closed
					&& modal.mfZoomSession
					&& modal.mfZoomSession.token === session.token;
			}

			function updateCounter(){
				counterEl.textContent = (currentIndex + 1) + ' / ' + allImages.length;
			}

			function setNavigationDisabled(disabled){
				prevBtn.disabled = disabled || !hasMultiple;
				nextBtn.disabled = disabled || !hasMultiple;
			}

			function updateCursor(){
				container.style.cursor = scale >= maxScale ? 'zoom-out' : 'zoom-in';
			}

			function applyTransform(smooth){
				image.style.transition = smooth ? 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none';
				image.style.transform = `scale(${scale}) translate(${translateX}px, ${translateY}px)`;
				updateCursor();
			}

			function clampTranslate(){
				if ( scale <= minScale ) {
					translateX = 0;
					translateY = 0;
					return;
				}
				const imgRect = image.getBoundingClientRect();
				const contRect = container.getBoundingClientRect();
				const scaledW = image.naturalWidth ? image.offsetWidth * scale : imgRect.width;
				const scaledH = image.naturalHeight ? image.offsetHeight * scale : imgRect.height;
				const maxX = Math.max(0, (scaledW - contRect.width) / (2 * scale));
				const maxY = Math.max(0, (scaledH - contRect.height) / (2 * scale));
				translateX = Math.max(-maxX, Math.min(maxX, translateX));
				translateY = Math.max(-maxY, Math.min(maxY, translateY));
			}

			function showHint(){
				hint.classList.add('visible');
				clearTimeout(session.hintTimer);
				session.hintTimer = setTimeout(()=>{
					if ( sessionIsCurrent() ) hint.classList.remove('visible');
				}, 2200);
			}

			function hideHint(){
				hint.classList.remove('visible');
				clearTimeout(session.hintTimer);
			}

			function onWheel(event){
				event.preventDefault();
				const rect = container.getBoundingClientRect();
				const mouseX = event.clientX - rect.left - rect.width / 2;
				const mouseY = event.clientY - rect.top - rect.height / 2;
				const delta = event.deltaY > 0 ? -0.25 : 0.25;
				const previousScale = scale;
				scale = Math.min(maxScale, Math.max(minScale, scale + delta));

				if ( scale !== previousScale ) {
					translateX = mouseX / scale - mouseX / previousScale + translateX;
					translateY = mouseY / scale - mouseY / previousScale + translateY;
				}
				if ( scale <= minScale ) {
					translateX = 0;
					translateY = 0;
				}
				clampTranslate();
				applyTransform(false);
				hideHint();
			}

			function onDblClick(event){
				event.preventDefault();
				if ( scale > minScale ) {
					scale = minScale;
					translateX = 0;
					translateY = 0;
				} else {
					const rect = container.getBoundingClientRect();
					const clickX = event.clientX - rect.left - rect.width / 2;
					const clickY = event.clientY - rect.top - rect.height / 2;
					scale = 2.5;
					translateX = -clickX / scale;
					translateY = -clickY / scale;
					clampTranslate();
				}
				applyTransform(true);
				hideHint();
			}

			function onPointerDown(event){
				if ( event.pointerType === 'touch' || scale <= minScale ) return;
				isDragging = true;
				dragStartX = event.clientX;
				dragStartY = event.clientY;
				startTransX = translateX;
				startTransY = translateY;
				container.style.cursor = 'grabbing';
				image.style.transition = 'none';
			}

			function onPointerMove(event){
				if ( !isDragging || event.pointerType === 'touch' ) return;
				translateX = startTransX + (event.clientX - dragStartX) / scale;
				translateY = startTransY + (event.clientY - dragStartY) / scale;
				clampTranslate();
				applyTransform(false);
				container.style.cursor = 'grabbing';
			}

			function onPointerUp(event){
				if ( event.pointerType === 'touch' ) return;
				isDragging = false;
				updateCursor();
			}

			function getTouchDist(touches){
				return Math.hypot(
					touches[0].clientX - touches[1].clientX,
					touches[0].clientY - touches[1].clientY
				);
			}

			function onTouchStart(event){
				if ( event.touches.length === 2 ) {
					event.preventDefault();
					lastTouchDist = getTouchDist(event.touches);
					pinchStartScale = scale;
					isSwiping = false;
				} else if ( event.touches.length === 1 ) {
					const now = Date.now();
					if ( now - lastTapTime < 300 ) {
						event.preventDefault();
						onDblClick({
							preventDefault: ()=>{},
							clientX: event.touches[0].clientX,
							clientY: event.touches[0].clientY
						});
						lastTapTime = 0;
						isSwiping = false;
						return;
					}
					lastTapTime = now;

					if ( scale > minScale ) {
						isDragging = true;
						dragStartX = event.touches[0].clientX;
						dragStartY = event.touches[0].clientY;
						startTransX = translateX;
						startTransY = translateY;
						image.style.transition = 'none';
						isSwiping = false;
					} else if ( hasMultiple ) {
						isSwiping = true;
						swipeStartX = event.touches[0].clientX;
						swipeEndX = event.touches[0].clientX;
					}
				}
			}

			function onTouchMove(event){
				if ( event.touches.length === 2 ) {
					event.preventDefault();
					const distance = getTouchDist(event.touches);
					const scaleDelta = distance / lastTouchDist;
					scale = Math.min(maxScale, Math.max(minScale, pinchStartScale * scaleDelta));
					if ( scale <= minScale ) {
						translateX = 0;
						translateY = 0;
					}
					clampTranslate();
					applyTransform(false);
					hideHint();
				} else if ( event.touches.length === 1 && isDragging ) {
					event.preventDefault();
					translateX = startTransX + (event.touches[0].clientX - dragStartX) / scale;
					translateY = startTransY + (event.touches[0].clientY - dragStartY) / scale;
					clampTranslate();
					applyTransform(false);
				} else if ( event.touches.length === 1 && isSwiping ) {
					swipeEndX = event.touches[0].clientX;
					if ( Math.abs(swipeEndX - swipeStartX) > 15 ) event.preventDefault();
				}
			}

			function onTouchEnd(event){
				if ( event.touches.length < 2 ) {
					lastTouchDist = 0;
					pinchStartScale = scale;
				}
				if ( event.touches.length === 0 ) {
					if ( isSwiping && hasMultiple ) {
						const distance = swipeEndX - swipeStartX;
						if ( Math.abs(distance) > 50 ) {
							navigateTo(distance > 0 ? currentIndex - 1 : currentIndex + 1);
						}
						isSwiping = false;
					}
					isDragging = false;
				}
			}

			function bindGestures(){
				if ( session.gesturesBound ) return;
				container.addEventListener('wheel', onWheel, { passive: false });
				container.addEventListener('dblclick', onDblClick);
				container.addEventListener('pointerdown', onPointerDown);
				document.addEventListener('pointermove', onPointerMove);
				document.addEventListener('pointerup', onPointerUp);
				container.addEventListener('touchstart', onTouchStart, { passive: false });
				container.addEventListener('touchmove', onTouchMove, { passive: false });
				container.addEventListener('touchend', onTouchEnd);
				session.gesturesBound = true;
			}

			function unbindGestures(){
				if ( !session.gesturesBound ) return;
				container.removeEventListener('wheel', onWheel);
				container.removeEventListener('dblclick', onDblClick);
				container.removeEventListener('pointerdown', onPointerDown);
				document.removeEventListener('pointermove', onPointerMove);
				document.removeEventListener('pointerup', onPointerUp);
				container.removeEventListener('touchstart', onTouchStart);
				container.removeEventListener('touchmove', onTouchMove);
				container.removeEventListener('touchend', onTouchEnd);
				session.gesturesBound = false;
			}

			function loadImage(index){
				currentIndex = index;
				updateCounter();
				scale = 1;
				translateX = 0;
				translateY = 0;
				setNavigationDisabled(true);
				modal.classList.remove('loaded');
				errorEl.hidden = true;
				errorText.textContent = '';
				retryBtn.disabled = true;
				image.hidden = false;
				image.style.transform = '';
				image.style.transition = '';
				image.alt = allImages[currentIndex].alt;

				session.loadRevision += 1;
				const loadRevision = session.loadRevision;
				const requestedSrc = allImages[currentIndex].src;
				const requestedAbsoluteSrc = new URL(requestedSrc, document.baseURI).href;
				function onImageReady(){
					if ( !sessionIsCurrent()
						|| loadRevision !== session.loadRevision
						|| (image.currentSrc !== requestedAbsoluteSrc && image.src !== requestedAbsoluteSrc) ) return;
					image.onload = null;
					image.onerror = null;
					errorEl.hidden = true;
					errorText.textContent = '';
					applyTransform(false);
					modal.classList.add('loaded');
					setNavigationDisabled(false);
					bindGestures();
					showHint();
				}

				image.onload = onImageReady;
				image.onerror = ()=>{
					if ( !sessionIsCurrent() || loadRevision !== session.loadRevision ) return;
					image.onload = null;
					image.onerror = null;
					image.hidden = true;
					errorText.textContent = owner.dataset.errorLabel || '';
					errorEl.hidden = false;
					retryBtn.disabled = false;
					modal.classList.add('loaded');
					setNavigationDisabled(false);
					unbindGestures();
					hideHint();
				};
				image.src = requestedSrc;
				if ( image.complete && image.naturalWidth > 0 ) queueMicrotask(onImageReady);
			}

			function navigateTo(index){
				if ( !sessionIsCurrent() || !hasMultiple ) return;
				if ( index < 0 ) index = allImages.length - 1;
				if ( index >= allImages.length ) index = 0;
				loadImage(index);
			}

			function onPrevClick(event){
				event.stopPropagation();
				if ( !prevBtn.disabled ) navigateTo(currentIndex - 1);
			}

			function onNextClick(event){
				event.stopPropagation();
				if ( !nextBtn.disabled ) navigateTo(currentIndex + 1);
			}

			function onRetryClick(event){
				event.stopPropagation();
				if ( retryBtn.disabled ) return;
				retryBtn.disabled = true;
				loadImage(currentIndex);
			}

			function onKeyDown(e){
				if ( e.repeat && e.key !== 'Tab' ) {
					if ( ['Enter', ' ', 'Escape', 'ArrowLeft', 'ArrowRight'].includes(e.key) ) e.preventDefault();
					return;
				}
				if ( e.key === 'Escape' ) {
					e.preventDefault();
					close();
					return;
				}
				if ( e.key === 'Tab' ) {
					const focusable = owner.getFocusable();
					const first = focusable[0];
					const last = focusable[focusable.length - 1];
					if ( !first ) {
						e.preventDefault();
						modal.focus();
					} else if ( e.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement)) ) {
						e.preventDefault();
						last.focus();
					} else if ( !e.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement)) ) {
						e.preventDefault();
						first.focus();
					}
				}
				if ( hasMultiple && !prevBtn.disabled && !nextBtn.disabled && scale <= minScale ) {
					if ( e.key === 'ArrowLeft' ) {
						e.preventDefault();
						navigateTo(currentIndex - 1);
					} else if ( e.key === 'ArrowRight' ) {
						e.preventDefault();
						navigateTo(currentIndex + 1);
					}
				}
			}

			function close(restoreFocus = true){
				if ( session.closed ) return;
				session.closed = true;
				const closingToken = session.token;
				const returnFocus = owner.returnFocus;

				clearTimeout(session.hintTimer);
				image.onload = null;
				image.onerror = null;
				document.removeEventListener('keydown', onKeyDown);
				backdrop.removeEventListener('click', close);
				closeBtn.removeEventListener('click', close);
				prevBtn.removeEventListener('click', onPrevClick);
				nextBtn.removeEventListener('click', onNextClick);
				retryBtn.removeEventListener('click', onRetryClick);
				unbindGestures();
				hideHint();

				modal.classList.remove('opened');
				modal.classList.remove('loaded');
				errorEl.hidden = true;
				errorText.textContent = '';
				modal.setAttribute('aria-hidden', 'true');
				owner.setBackgroundInert(false);
				document.body.style.overflow = previousBodyOverflow;

				if ( modal.mfZoomSession && modal.mfZoomSession.token === closingToken ) {
					modal.mfZoomSession = null;
				}
				owner.returnFocus = null;
				if ( restoreFocus && returnFocus && returnFocus.isConnected ) {
					returnFocus.focus({ preventScroll: true });
				}

				setTimeout(()=>{
					if ( modal.mfZoomSession && modal.mfZoomSession.token !== closingToken ) return;
					image.removeAttribute('src');
					image.alt = '';
					image.style.transform = '';
					image.style.transition = '';
				}, 300);
			}

			session.close = close;
			owner.returnFocus = owner.trigger;
			const previousBodyOverflow = document.body.style.overflow;
			setNavigationDisabled(true);
			modal.setAttribute('aria-hidden', 'false');
			modal.classList.add('opened');
			owner.setBackgroundInert(true);
			document.body.style.overflow = 'hidden';

			document.addEventListener('keydown', onKeyDown);
			backdrop.addEventListener('click', close);
			closeBtn.addEventListener('click', close);
			prevBtn.addEventListener('click', onPrevClick);
			nextBtn.addEventListener('click', onNextClick);
			retryBtn.addEventListener('click', onRetryClick);
			closeBtn.focus({ preventScroll: true });
			setTimeout(()=>{
				if ( sessionIsCurrent() && !modal.contains(document.activeElement) ) {
					closeBtn.focus({ preventScroll: true });
				}
			}, 50);
			loadImage(currentIndex);
		}

	}

	if ( typeof customElements.get('product-image-zoom') == 'undefined' ) {
		customElements.define('product-image-zoom', ProductImageZoom);
	}

}
